import React, {useState} from 'react';
import { useParams } from 'react-router-dom';
import DataTable from '@preflighttech/preflight-tables';
import { View, Text, Pressable } from 'react-native';
import {
  SELLER_USERS_WITH_COUNT,
  HIDE_SELLER_USER,
  UNHIDE_SELLER_USER,
  REINVITE_SELLER_USER
} from './queries';
import { useMutation } from '@apollo/client';
import SubNav from './SubNav';
import {
  MainContainer,
  ContentContainer,
  LinkContainer,
} from '../../elements';
import Modal from '../../elements/Modal';
import EditSellerUser from './edit';
import Filter from 'app/src/elements/Filter';
import { ResetSettings } from 'app/src/elements/Pagination/ResetSettings';

const inputStyle = {
  marginBottom: '10px'
}

export const NOTIFICATION_OPTIONS = [
  {
    value: 'assigned_only',
    label: 'Only Assets assigned to me'
  },
  {
    value: 'include_unassigned',
    label: 'Assets assigned to me, and Assets not assigned to anyone',
  },
  {
    value: 'all',
    label: 'All assets',
  },
  {
    value: 'sales_report_only',
    label: 'Only Sales Reports',
  },
];

const SellerUsers = props => {
  const params = useParams();
  const sellerId = params.sellerId;
  const [refetch, setRefetch] = useState();
  const [clickedSellerUser, setClickedSellerUser] = useState();
  const [showEditSellerUserModal, setShowEditSellerUserModal] = useState(false);
  const [filterOptions, setFilterOptions] = useState();
  const [filters, setFilters] = useState({});
  const [settings, setSettings] = useState({});
  const queryVariables = {
    sellerId: sellerId,
    filterColumns: [],
    filterNames: [],
    scopes: [],
    sellerId,
  };
  const [hideSellerUser] = useMutation(
    HIDE_SELLER_USER, { onCompleted: refetch }
  )

  const [unHideSellerUser] = useMutation(
    UNHIDE_SELLER_USER, { onCompleted: refetch }
  )

  const onToggleHide = (sellerUserId, isHidden) => {
    if(isHidden) {
      unHideSellerUser({ variables: { id: sellerUserId }});
    }else {
      hideSellerUser({ variables: { id: sellerUserId }});
    }
  };

  const [resendSellerUserInvite] = useMutation(
    REINVITE_SELLER_USER, { onCompleted: refetch }
  )

  const onResendInvite = (sellerUserId) => {
    resendSellerUserInvite({ variables: { id: sellerUserId }});
  };

  const setFilter = (key, value) => {
    const currentFilters = {...filters};
    currentFilters[key] = value;
    setFilters(currentFilters);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  const columns = [
    {
      key: 'email',
      label: 'Email',
      sort: 'asc',
    },
    {
      key: 'firstName',
      label: 'First Name',
    },
    {
      key: 'lastName',
      label: 'Last Name',
    },
    {
      key: 'role',
      label: 'Role',
    },
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      content: ({ entry }) => {
        return (
          <View style={{display: 'inline-flex'}}>
            <LinkContainer>
              <Pressable
                onPress={() => {
                  setClickedSellerUser(entry);
                  setShowEditSellerUserModal(true)
                }}
                style={{marginLeft: '10px'}}
                key={`user-edit-link-${entry.id}`}
                name={`user-edit-link-${entry.id}`}
              >
                <Text>
                  Edit
                </Text>
              </Pressable>
              <Text>{` | `}</Text>
              <Pressable
                onPress={() => {
                  if(window.confirm(
                    `Make ${entry.firstName} ${entry.lastName} ${entry.isHidden ? 'active' : 'inactive'}?`,
                  )){
                    onToggleHide(entry.id, entry.isHidden)
                  }
                }}
                key={`user-hide-link-${entry.id}`}
                  name={`user-hide-link-${entry.id}`}
              >
                <Text>
                  {
                    `make ${entry.isHidden ? 'active' : 'inactive'}`
                  }
                </Text>
              </Pressable>
              <Text>{` | `}</Text>
              <Pressable
                onPress={() => {
                  if(window.confirm(
                    entry.invitationAcceptedAt ?
                      `Send password reset to ${entry.email}?` :
                      `Resend account invite to ${entry.email}?`
                  )) {
                    onResendInvite(entry.id)
                  }
                }}
                key={`user-invite-link-${entry.id}`}
                name={`user-invite-link-${entry.id}`}
              >
                <Text>
                  {entry.invitationAcceptedAt ?
                    'send password reset' : 'resend invite'
                  }
                </Text>
              </Pressable>
            </LinkContainer>
          </View>
        )
      }
    }
  ]

  const buttons = (
    <View style={{display: 'flex', flexDirection: 'row'}}>
      <Filter
        clearFilters={() => {
          setFilters({})
          setShowModal(false)
        }}
        filters={filters}
        setFilters={setFilters}
      >
        <View style={{padding: '20px', justifyContent: 'space-between'}}>
          <Text>Show Inactive?</Text>
          <input
            type={'checkbox'}
            name={'showHidden'}
            onChange={e => {
              setFilter('showHidden', e.target.checked)
            }}
            checked={filters.showHidden || false}
            style={{
              marginTop: '-14px',
              marginLeft: '-80px',
              zIndex: 1000,
              ...inputStyle
            }}
          />
          <Text>First Name:</Text>
          <input
            type={'text'}
            name="firstName"
            onChange={e => setFilter('firstName', e.target.value)}
            value={filters.firstName || ''}
            style={inputStyle}
          />
          <Text>Last Name:</Text>
          <input
            type={'text'}
            name="lastName"
            onChange={e => setFilter('lastName', e.target.value)}
            value={filters.lastName || ''}
            style={inputStyle}
          />
        </View>
      </Filter>
      <ResetSettings />
    </View>
  );

  return (
    <MainContainer>
      <SubNav
        refetch={refetch}
        modalType={'Add User'}
      />
      <ContentContainer>
        <DataTable.Gql
          setRefetch={setRefetch}
          query={SELLER_USERS_WITH_COUNT}
          queryName='sellerUsersWithCount'
          columns={columns}
          pageLength={25}
          buttons={buttons}
          lengthMenu={ [10, 25, 50, 'All'] }
          setSettings={setSettings}
          dataUpdated={data => setFilterOptions(data?.sellerUsersWithCount)}
          htmlTable
          queryVariables={queryVariables}
        />
      </ContentContainer>
      {showEditSellerUserModal &&
        <Modal
          showModal={showEditSellerUserModal}
          setShowModal={setShowEditSellerUserModal}
          modalType={'Edit Seller User'}
          objectName={`${clickedSellerUser.firstName} ${clickedSellerUser.lastName}`}
          user={clickedSellerUser}
        >
          <EditSellerUser
            setShowModal={setShowEditSellerUserModal}
            refetch={refetch}
            sellerUser={clickedSellerUser}
          />
        </Modal>
      }
    </MainContainer>
  )
}

export default SellerUsers;
